import * as React from "react";
import { CSSProperties, useRef } from "react";
import { Input, InputProps } from "./Input";

const sizerStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    visibility: "hidden",
    zIndex: -1,
    overflow: "hidden",
    whiteSpace: "pre",
};

type Props = InputProps & {
    minWidth?: number;
    parentRef?: React.RefObject<HTMLDivElement>;
};

const copyStyles = (styles: CSSStyleDeclaration, node: HTMLDivElement) => {
    node.style.fontSize = styles.fontSize;
    node.style.fontFamily = styles.fontFamily;
    node.style.fontWeight = styles.fontWeight;
    node.style.fontStyle = styles.fontStyle;
    node.style.letterSpacing = styles.letterSpacing;
    node.style.height = styles.height;
    node.style.lineHeight = styles.lineHeight;
    node.style.minWidth = styles.minWidth;
    node.style.maxWidth = styles.maxWidth;
    node.style.textTransform = styles.textTransform;
    node.style.paddingTop = styles.paddingTop;
    node.style.paddingRight = styles.paddingRight;
    node.style.paddingBottom = styles.paddingBottom;
    node.style.paddingLeft = styles.paddingLeft;
    node.style.borderTopWidth = styles.borderTopWidth;
    node.style.borderTopStyle = styles.borderTopStyle;
    node.style.borderTopColor = styles.borderTopColor;
    node.style.borderRightWidth = styles.borderRightWidth;
    node.style.borderRightStyle = styles.borderRightStyle;
    node.style.borderRightColor = styles.borderRightColor;
    node.style.borderBottomWidth = styles.borderBottomWidth;
    node.style.borderBottomStyle = styles.borderBottomStyle;
    node.style.borderBottomColor = styles.borderBottomColor;
    node.style.borderLeftWidth = styles.borderLeftWidth;
    node.style.borderLeftStyle = styles.borderLeftStyle;
    node.style.borderLeftColor = styles.borderLeftColor;
    node.style.boxSizing = styles.boxSizing;
};

const getWidthOffset = (element: HTMLDivElement) => {
    const { marginRight, marginLeft, paddingLeft, paddingRight } = window.getComputedStyle(element);
    return parseFloat(marginRight) + parseFloat(marginLeft) + parseFloat(paddingRight) + parseFloat(paddingLeft);
};

const getMaxWidth = (parent: HTMLDivElement) =>
    parent && parent.clientWidth - getWidthOffset(parent) - getWidthOffset(parent);

export const AutoSizeInput: React.FC<Props> = props => {
    const { value, placeholder, minWidth = 0, parentRef, style } = props;
    const inputRef = React.useRef<HTMLInputElement>();
    const inputComponentRef = React.useRef<Input>();
    const sizerRef = React.useRef<HTMLDivElement>();
    const placeHolderSizerRef = React.useRef<HTMLDivElement>();
    const [inputWidth, setInputWidth] = React.useState<number>(minWidth);
    const ratio = React.useRef<number>(0);

    const refCallback = (ref: HTMLInputElement) => {
        inputRef.current = ref;
    };

    const wrapperRef = useRef<HTMLDivElement>();

    const copyInputStyles = () => {
        if (!window.getComputedStyle) {
            return;
        }

        const inputStyles = inputRef?.current && window.getComputedStyle(inputRef.current);

        if (!inputStyles) {
            return;
        }

        ratio.current = Math.ceil(parseInt(inputStyles.fontSize, 10)) || 0;

        copyStyles(inputStyles, sizerRef.current);

        if (placeHolderSizerRef?.current) {
            copyStyles(inputStyles, placeHolderSizerRef.current);
        }
    };

    const updateInputWidth = () => {
        if (typeof sizerRef?.current?.scrollWidth === "undefined") {
            return;
        }

        let newInputWidth;
        const maxWidth = getMaxWidth(parentRef?.current);

        if (placeholder && placeHolderSizerRef?.current?.scrollWidth) {
            newInputWidth =
                Math.max(sizerRef.current.scrollWidth, placeHolderSizerRef.current.scrollWidth) + ratio.current;
        } else {
            newInputWidth = sizerRef.current.scrollWidth + ratio.current;
        }

        if (newInputWidth < minWidth) {
            newInputWidth = minWidth;
        } else if (newInputWidth >= maxWidth) {
            newInputWidth = maxWidth;
        }

        if (newInputWidth !== inputWidth) {
            setInputWidth(newInputWidth);
        }
    };

    React.useEffect(() => {
        if (props.refCallback) {
            props.refCallback(inputComponentRef.current);
        }
    }, []);

    React.useEffect(() => {
        copyInputStyles();
        updateInputWidth();
    }, [value, placeholder]);

    const inputStyle: CSSProperties = React.useMemo(
        () => ({
            width: `${inputWidth}px`,
            maxWidth: getMaxWidth(parentRef?.current) ?? "none",
            minWidth,
        }),
        [inputWidth]
    );

    const wrapperStyle: CSSProperties = React.useMemo(
        () => ({
            ...style,
            lineHeight: 0,
            display: "inline-block",
            verticalAlign: "top",
        }),
        []
    );

    return (
        <div style={wrapperStyle} ref={wrapperRef}>
            <Input {...props} ref={inputComponentRef} refCallback={refCallback} style={inputStyle} />
            <div ref={sizerRef} style={sizerStyle}>
                {value}
            </div>
            {placeholder && (
                <div data-testid="placeholder" ref={placeHolderSizerRef} style={sizerStyle}>
                    {placeholder}
                </div>
            )}
        </div>
    );
};
